// Řádek signálu hlídače lhůt (4.10) — společný pro sekci Signály na Domů a drawer Všechny signály:
// tečka závažnosti (barva + popisek pro čtečku), nadpis, druh · termín slovy („za 12 dní“, „po termínu 3 dny“)
// s datem, podrobnosti a návrh kroku ze serveru, odkaz „Otevřít“ a tlačítko „Vyřešeno“ (aplikační dialog
// s nepovinnou poznámkou → POST /signaly/{id}/vyresit). Vede‑li odkaz rovnou na formulář (`odkaz_akce`), tlačítko nese
// název akce („Odběr proveden“) — rozhodnutí 27. 10. 2026. Signál určený někomu jinému (`muze.vyresit` bez práva) tlačítko
// nemá vůbec — rozhodnutí 28. 9. 2026; „Otevřít“ se nevykreslí, když účet cílovou sekci nemá.
// Vyřešený signál je šedý s poznámkou, jménem a časem. Bez Undo — server vrátí podruhé 409.
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { duvodChyby } from '../api/klient';
import {
  DRUHY_S_DUVODEM,
  KLIC_PREHLED,
  obnovitPrehled,
  popisDruhu,
  popisTerminu,
  type RanniPrehled,
  type Signal,
  vyresitSignal,
  ZAVAZNOSTI_SIGNALU,
} from '../api/prehled';
import { ignorovatNalez, obnovitKontroly, odebratVyjimku } from '../api/kontroly-souladu';
import { obnovitRytmus, odlozitSvoz, zrusitOdlozeni } from '../api/rytmus-svozu';
import { formatDatum, formatDatumCas } from '../formaty';
import { useDialog } from '../komponenty/Dialog';
import { jeSkryte } from '../komponenty/prava';
import { TlacitkoSDuvodem } from '../komponenty/TlacitkoSDuvodem';
import { useToast } from '../komponenty/Toast';
import { usePrihlaseni } from '../prihlaseni/stav-prihlaseni';
import { odkazProUcet, smiOtevritOdkaz } from './odkazy';

/** Co napsat do důvodu u signálů, které se vyřeší jen s důvodem (`DRUHY_S_DUVODEM`). */
const TEXT_DUVODU: Readonly<Record<string, string>> = {
  zakaznik_prestal_odebirat:
    'Napište, proč zákazník neodebírá (ukončil spolupráci, sezónní odběr…). Pro stejnou poslední fakturu se signál už nevrátí.',
  tankovani_vypis_mesic:
    'Napište, proč výpis za tento měsíc nebude (například „MOL za měsíc nic nefakturoval“). Za stejný měsíc se signál už nevrátí.',
};

/** Otevře dialog, po potvrzení označí signál jako vyřešený; vrací true, když se zapsalo. */
export function useVyresitSignal() {
  const dialog = useDialog();
  const toast = useToast();
  const queryClient = useQueryClient();

  return async (s: Signal): Promise<boolean> => {
    const sDuvodem = DRUHY_S_DUVODEM.has(s.druh);
    const poznamka = await dialog.potvrdit({
      titul: 'Označit signál jako vyřešený?',
      text: sDuvodem
        ? `${s.nadpis} ${TEXT_DUVODU[s.druh] ?? 'Napište důvod.'}`
        : `${s.nadpis} Signál zmizí z přehledu; když příčina trvá, hlídač ho po čase založí znovu.`,
      tlacitko: 'Vyřešeno',
      nebezpecne: false,
      duvod: sDuvodem ? 'Důvod' : 'Poznámka (nepovinná)',
      duvodPovinny: sDuvodem,
    });
    if (poznamka === null) return false;
    try {
      await vyresitSignal(s.id, poznamka);
      // Z přehledu na Domů řádek zmizí hned (bez čekání na server), pak se vše načte znovu.
      queryClient.setQueriesData<RanniPrehled>({ queryKey: KLIC_PREHLED }, (puvodni) =>
        puvodni
          ? {
              ...puvodni,
              signaly: puvodni.signaly.filter((x) => x.id !== s.id),
              // Neodeslané zápisy v tabletech mají v přehledu vlastní blok (05/136).
              neodeslane_zapisy: (puvodni.neodeslane_zapisy ?? []).filter((x) => x.id !== s.id),
              signaly_celkem: Math.max(0, (puvodni.signaly_celkem ?? puvodni.signaly.length) - 1),
            }
          : puvodni,
      );
      obnovitPrehled(queryClient);
      toast.ukazat('Signál označen jako vyřešený');
      return true;
    } catch (e) {
      toast.ukazat(duvodChyby(e));
      obnovitPrehled(queryClient);
      return false;
    }
  };
}

/**
 * „Ignorovat…“ u nálezu kontroly souladu (krok 12.3): dialog s povinným důvodem → výjimka; Zpět výjimku odebere
 * a nález se zase ohlásí. Vrací true, když se zapsalo.
 */
export function useIgnorovatNalez() {
  const dialog = useDialog();
  const toast = useToast();
  const queryClient = useQueryClient();

  return async (s: Signal): Promise<boolean> => {
    const duvod = await dialog.potvrdit({
      titul: 'Ignorovat tento nález?',
      text: `${s.nadpis} Nález se přestane hlásit. Výjimku najdete a odeberete v Nastavení › Kontroly souladu.`,
      tlacitko: 'Ignorovat',
      nebezpecne: false,
      duvod: 'Proč se nález ignoruje (povinné)',
      duvodPovinny: true,
    });
    if (duvod === null) return false;
    try {
      const vysledek = await ignorovatNalez(s.id as string, duvod);
      obnovitPrehled(queryClient);
      obnovitKontroly(queryClient);
      const idVyjimky = vysledek.vyjimka.id as string;
      toast.ukazat('Nález ignorován', {
        zpet: () => {
          void odebratVyjimku(idVyjimky)
            .then(() => {
              obnovitPrehled(queryClient);
              obnovitKontroly(queryClient);
            })
            .catch((e: unknown) => toast.ukazat(duvodChyby(e)));
        },
      });
      return true;
    } catch (e) {
      toast.ukazat(duvodChyby(e));
      obnovitPrehled(queryClient);
      return false;
    }
  };
}

/**
 * „Odložit…“ u signálu „zákazník po termínu svozu“ (rytmus svozů, 10. 10. 2026): dialog s povinným důvodem →
 * odložení o polovinu obvyklého intervalu (aspoň týden, spočítá server) a signál se vyřeší; Zpět odložení zruší.
 * Jiné datum jde vybrat v Požadavky na svoz › Navrhované svozy.
 */
export function useOdlozitSvoz() {
  const dialog = useDialog();
  const toast = useToast();
  const queryClient = useQueryClient();

  return async (s: Signal): Promise<boolean> => {
    if (!s.entita_id) return false;
    const duvod = await dialog.potvrdit({
      titul: 'Odložit svoz?',
      text: `${s.nadpis} Svoz se odloží o polovinu obvyklého intervalu (aspoň o týden); jiné datum vyberete v Navrhovaných svozech.`,
      tlacitko: 'Odložit',
      nebezpecne: false,
      duvod: 'Proč svoz teď není potřeba (povinné)',
      duvodPovinny: true,
    });
    if (duvod === null) return false;
    try {
      const v = await odlozitSvoz({ zakaznik_id: s.entita_id as string, duvod });
      obnovitPrehled(queryClient);
      obnovitRytmus(queryClient);
      toast.ukazat(`Svoz odložen do ${formatDatum(v.do_dne)}`, {
        zpet: () => {
          void zrusitOdlozeni(v.id)
            .then(() => {
              obnovitPrehled(queryClient);
              obnovitRytmus(queryClient);
            })
            .catch((e: unknown) => toast.ukazat(duvodChyby(e)));
        },
      });
      return true;
    } catch (e) {
      toast.ukazat(duvodChyby(e));
      obnovitPrehled(queryClient);
      return false;
    }
  };
}

interface Props {
  signal: Signal;
  /** Po úspěšném vyřešení (drawer si seznam obnoví). */
  naVyreseno?: (s: Signal) => void;
}

export function SignalRadek({ signal: s, naVyreseno }: Props) {
  const vyresit = useVyresitSignal();
  const ignorovat = useIgnorovatNalez();
  const odlozitSvozDialog = useOdlozitSvoz();
  const { ja } = usePrihlaseni();
  const [pracuji, setPracuji] = useState(false);
  const vyreseny = s.stav === 'vyreseny';
  const odkaz = odkazProUcet(ja, s.odkaz);
  const kOtevreni = smiOtevritOdkaz(ja, odkaz);
  const kVyreseni = !vyreseny && !jeSkryte(s.muze.vyresit);
  const termin = popisTerminu(s.za_dni);
  const poTerminu = (s.za_dni ?? 0) < 0;

  const naVyresit = async () => {
    setPracuji(true);
    try {
      if (await vyresit(s)) naVyreseno?.(s);
    } finally {
      setPracuji(false);
    }
  };

  const naIgnorovat = async () => {
    setPracuji(true);
    try {
      if (await ignorovat(s)) naVyreseno?.(s);
    } finally {
      setPracuji(false);
    }
  };
  const naOdlozitSvoz = async () => {
    setPracuji(true);
    try {
      if (await odlozitSvozDialog(s)) naVyreseno?.(s);
    } finally {
      setPracuji(false);
    }
  };
  // Odložit svoz s důvodem (rytmus svozů): stejné pravidlo jako u ignorování — bez práva nebo cizí signál nic.
  const kOdlozeni =
    !vyreseny &&
    s.druh === 'zakaznik_po_terminu_svozu' &&
    s.muze.odlozit_svoz !== undefined &&
    !jeSkryte(s.muze.odlozit_svoz);
  // Kdo právo ignorovat nemá, nebo mu nález není určen, tlačítko nevidí vůbec (rozhodnutí 28. 9. 2026);
  // vyřešený nález už ignorovat nejde, tlačítko tu zůstane neaktivní s důvodem.
  const kIgnorovani =
    !vyreseny &&
    s.druh === 'kontrola_souladu' &&
    s.muze.ignorovat !== undefined &&
    !jeSkryte(s.muze.ignorovat);

  return (
    <li
      className={`signal signal--${s.zavaznost}${vyreseny ? ' signal--vyreseny' : ''}`}
      data-testid="signal-radek"
      data-druh={s.druh}
      data-stav={s.stav}
    >
      <span
        className={`signal__tecka signal__tecka--${vyreseny ? 'vyreseny' : s.zavaznost}`}
        role="img"
        aria-label={vyreseny ? 'Vyřešený' : ZAVAZNOSTI_SIGNALU[s.zavaznost]}
      />
      <div className="signal__obsah">
        <span className="signal__nadpis">{s.nadpis}</span>
        <span className="signal__meta">
          <span>{popisDruhu(s)}</span>
          {termin && (
            <span className={`signal__termin${poTerminu ? ' signal__termin--po' : ''}`}>
              {termin}
              {s.termin ? ` (${formatDatum(s.termin)})` : ''}
            </span>
          )}
          {s.komu_jmeno && <span>{s.komu_jmeno}</span>}
        </span>
        {s.text && <span className="signal__text">{s.text}</span>}
        {s.navrh_kroku && !vyreseny && (
          <span className="signal__krok">
            <span className="signal__krok-popisek">Další krok:</span> {s.navrh_kroku}
          </span>
        )}
        {vyreseny && (
          <span className="signal__vyreseno" data-testid="signal-vyreseno">
            Vyřešeno {formatDatumCas(s.vyreseno_kdy)}
            {s.vyreseno_jak === 'automaticky' ? ' automaticky (příčina pominula)' : ''}
            {s.vyresil_jmeno ? ` · ${s.vyresil_jmeno}` : ''}
            {s.poznamka ? ` · „${s.poznamka}“` : ''}
          </span>
        )}
      </div>
      {(kOtevreni || kIgnorovani || kOdlozeni || kVyreseni) && (
        <div className="signal__akce">
          {kOtevreni && odkaz && (
            // Tlačítko přímo v upozornění (27. 10. 2026): vede‑li odkaz rovnou na formulář, nese název akce
            // („Odběr proveden“) a otevře ho; jinak obecné „Otevřít“.
            <Link
              to={odkaz}
              className={`tlacitko ${s.odkaz_akce ? 'tlacitko--primarni' : 'tlacitko--sekundarni'} tlacitko--radek`}
              data-testid="signal-akce"
            >
              {s.odkaz_akce ?? 'Otevřít'}
            </Link>
          )}
          {kIgnorovani && s.muze.ignorovat && (
            <TlacitkoSDuvodem
              pravo={s.muze.ignorovat}
              varianta="sekundarni"
              className="tlacitko--radek"
              disabled={pracuji}
              onClick={() => void naIgnorovat()}
            >
              Ignorovat…
            </TlacitkoSDuvodem>
          )}
          {kOdlozeni && s.muze.odlozit_svoz && (
            <TlacitkoSDuvodem
              pravo={s.muze.odlozit_svoz}
              varianta="sekundarni"
              className="tlacitko--radek"
              disabled={pracuji}
              onClick={() => void naOdlozitSvoz()}
              data-testid="signal-odlozit-svoz"
            >
              Odložit…
            </TlacitkoSDuvodem>
          )}
          {kVyreseni && (
            <TlacitkoSDuvodem
              pravo={s.muze.vyresit}
              varianta="sekundarni"
              className="tlacitko--radek"
              disabled={pracuji}
              onClick={() => void naVyresit()}
            >
              {pracuji ? 'Ukládám…' : 'Vyřešeno'}
            </TlacitkoSDuvodem>
          )}
        </div>
      )}
    </li>
  );
}
